import { useCoreContext } from '@/core';
import { CourseLineEntity } from '@/domains/course-lines/entities';
import { OperationMethodTable } from '@/domains/operation-methods/entities';
import { Button, Form, Input, InputNumber, Select, Spin, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { CourseLineFormViewModel } from '../../viewmodels';
import { GetListOperationMethod } from '@/modules/operation-methods/viewmodels';

interface Props {
  detailCourseLine: CourseLineEntity;
  openForm: boolean;
  onFormSubmitSuccess?: () => void;
}

const categoriesOption = [
  { label: '18+', value: '18+' },
  { label: 'Kids', value: 'Kids' },
  { label: 'Teens', value: 'Teens' },
];

export const CourseLineForm = (props: Props) => {
  const { detailCourseLine, openForm, onFormSubmitSuccess } = props;
  const { loading } = useCoreContext();
  const [form] = Form.useForm();
  const { actionSaveCourseLine, validateForm } = CourseLineFormViewModel();
  const { schemaCourseLine } = validateForm({ detailCourseLine });
  const {
    operationMethodData,
    actionGetAllOperationMethod,
    loading: loadingOperationMethods,
  } = GetListOperationMethod();

  // Load operation methods on component mount
  useEffect(() => {
    actionGetAllOperationMethod();
  }, []);

  useEffect(() => {
    if (detailCourseLine && detailCourseLine.id) {
      form.setFieldsValue({
        id: detailCourseLine.id,
        name: detailCourseLine.name,
        description: detailCourseLine.description,
        category: detailCourseLine.category,
        basePrice: detailCourseLine.basePrice,
        operationMethods: detailCourseLine.operationMethods || [],
        isActive: detailCourseLine.isActive,
      });
    } else {
      form.resetFields();
    }
  }, [detailCourseLine, form, openForm]);

  const onFinish = async (values: any) => {
    const success = await actionSaveCourseLine({
      id: detailCourseLine?.id,
      ...values,
    });

    if (success) {
      // Reset form after successful submission
      form.resetFields();

      // Notify parent component about successful submission
      if (onFormSubmitSuccess) {
        onFormSubmitSuccess();
      }
    }
  };

  if (loading || loadingOperationMethods) {
    return <Spin />;
  }

  // Format Vietnamese number
  const formatNumber = (val: number | string | undefined): string => {
    if (val === undefined || val === null) return '';
    if (typeof val === 'string') val = Number(val);
    try {
      return val.toLocaleString('vi-VN');
    } catch (e) {
      return String(val);
    }
  };

  // Parse Vietnamese formatted number back to number
  const parseNumber = (val: string | undefined): number => {
    if (!val) return 0;
    return Number(val.replace(/\./g, ''));
  };

  return (
    <Form
      form={form}
      name='course-line-form'
      onFinish={onFinish}
      layout='vertical'
      initialValues={{ isActive: true, basePrice: 0, operationMethods: [] }}
    >
      <Form.Item
        name='name'
        label='Name'
        rules={[{ required: true, message: 'Please input the name!' }]}
      >
        <Input placeholder='Enter name' />
      </Form.Item>

      <Form.Item
        name='description'
        label='Description'
        rules={[{ required: true, message: 'Please input the description!' }]}
      >
        <Input.TextArea rows={4} placeholder='Enter description' />
      </Form.Item>

      <Form.Item
        name='category'
        label='Category'
        rules={[{ required: true, message: 'Please select the category!' }]}
      >
        <Select placeholder='Select a category'>
          {categoriesOption.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name='operationMethods' label='Operation Methods'>
        <Select
          mode='multiple'
          placeholder='Select operation methods'
          optionFilterProp='children'
          style={{ width: '100%' }}
        >
          {operationMethodData.map((method) => (
            <Select.Option key={method.id} value={method.id}>
              {method.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name='basePrice'
        label='Base Price'
        rules={[{ required: true, message: 'Please input the base price!' }]}
      >
        <InputNumber
          min={0}
          style={{ width: '100%' }}
          formatter={formatNumber}
          parser={(value) => parseNumber(value)}
          placeholder='Enter base price'
        />
      </Form.Item>

      <Form.Item name='isActive' label='Status' valuePropName='checked'>
        <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          {detailCourseLine?.id ? 'Update' : 'Create'}
        </Button>
      </Form.Item>
    </Form>
  );
};
